import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  Autocomplete,
  InputLabel,
  FormControl,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useEffect } from "react";
import Header from "../../../components/Header";
import { accountsmanagement, companyManagementUrl } from "../../../config";
import {
  allPurchaseInvoiceView,
  currency,
  allCompanies,
  allSuppliers,
  allAccounts,
  allTaxRates,
} from "../../../data/Axios/queries";
import {
  fetchDataEngine,
  generateAndExecuteMutation,
  graphqlQuery,
} from "../../../data/Axios/DynamicService";

const base_url = accountsmanagement.uri;
const company_baseurl = companyManagementUrl.uri;

const PurchaseInvoiceForm = (props) => {
  const { isEditing = false, data = {}, onClose, refetchData } = props;

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id) || null;

  // CHANGE 1: UPDATE INITIAL VALUES
  const initialValues = {
    id: data?.id || null,
    invoiceNumber: data?.invoiceNumber || "",
    supplierId: data?.supplierId || null,
    supplierName: data?.supplierName || "",
    loNumber: data?.loNumber || "",
    issueDate: data?.issueDate || new Date().toISOString().split("T")[0],
    dueDate: data?.dueDate || new Date().toISOString().split("T")[0],
    currencyId: data?.currencyId || "",
    exchangeRate: data?.exchangeRate || 1.0,
    totalAmount: data?.totalAmount || 0,
    statusId: data?.statusId || 1,
    companyId: data?.companyId || null,
    bankAccountId: data?.bankAccountId || null,
    withholdingTaxId: data?.withholdingTaxId || null,
    notes: data?.notes || "",
    createdBy: data?.createdBy || null,
    updatedBy: data?.updatedBy || null,
    isActive: data?.isActive ?? true,
  };

  const [suppliers, setSuppliers] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [withholdingTaxRates, setWithholdingTaxRates] = useState([]);

  const isNonMobile = useMediaQuery("(min-width:600px)");

  // CHANGE 3: UPDATE VALIDATION SCHEMA
  const validationSchema = yup.object().shape({
    invoiceNumber: yup.string().required("Invoice number required"),
    supplierId: yup.string().required("Supplier is required"),
    issueDate: yup.date().required("Issue Date is required"),
    dueDate: yup.date().required("Due Date is required"),
    currencyId: yup.string().required("Currency is required"),
    withholdingTaxId: yup.string().nullable(),
  });

  // CHANGE 4: UPDATE DATA FETCHING
  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const currencyData = await graphqlQuery(currency, base_url);
        setCurrencies(currencyData || []);

        const supplierData = await graphqlQuery(allSuppliers, base_url);
        setSuppliers(supplierData || []);

        const taxRateData = await graphqlQuery(allTaxRates, base_url);
        if (taxRateData) {
          const filteredTaxes = taxRateData.filter((tax) => tax.taxTypeId === 2);
          setWithholdingTaxRates(filteredTaxes);
        }
      } catch (error) {
        console.error("Error loading dropdown data:", error);
      }
    };
    fetchFormData();
  }, []);

  // CHANGE 5: UPDATE SUBMIT HANDLER
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const {
        invoiceNumber,
        supplierId,
        loNumber,
        issueDate,
        dueDate,
        currencyId,
        exchangeRate,
        totalAmount,
        statusId,
        companyId,
        bankAccountId,
        withholdingTaxId,
        notes,
      } = values;

      const baseMutationData = {
        invoiceNumber,
        supplierId: parseInt(supplierId),
        loNumber,
        issueDate,
        dueDate,
        currencyId: parseInt(currencyId),
        exchangeRate: parseFloat(exchangeRate),
        totalAmount: parseFloat(totalAmount),
        statusId,
        companyId,
        bankAccountId,
        withholdingTaxId: withholdingTaxId ? parseInt(withholdingTaxId, 10) : null,
        notes,
        isActive: values.isActive ? 1 : 0,
      };

      const mutationData = isEditing
        ? { ...baseMutationData, id: data?.id, updatedBy: userId }
        : { ...baseMutationData, createdBy: userId };

      const responseKey = isEditing ? "updatedPurchaseInvoice" : "newPurchaseInvoice";

      const response = await generateAndExecuteMutation(
        isEditing ? "updatePurchaseInvoice" : "createPurchaseInvoice",
        mutationData,
        responseKey,
        base_url
      );

      if (response) {
        onClose();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Purchase Invoice ${isEditing ? "updated" : "created"} successfully.`,
        }).then(() => {
          refetchData();

        });
      }
    } catch (error) {
      console.error("Submission error:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while saving the purchase invoice. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box m="20px">
      <Header
        title={isEditing ? "Edit Purchase Invoice" : "Create Purchase Invoice"}
        subtitle={
          isEditing
            ? "Edit an existing purchase invoice"
            : "Record a new purchase invoice"
        }
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              <TextField
                fullWidth
                variant="filled"
                label="Invoice Number"
                name="invoiceNumber"
                value={values.invoiceNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{ gridColumn: "span 2" }}
              />

              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Supplier *</InputLabel>
                <Select
                  name="supplierId"
                  value={values.supplierId || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.supplierId && !!errors.supplierId}
                >
                  {suppliers.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.supplierName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                variant="filled"
                label="LPO/LO Number"
                name="loNumber"
                value={values.loNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                type="date"
                label="Issue Date *"
                name="issueDate"
                value={values.issueDate}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.issueDate && !!errors.issueDate}
                helperText={touched.issueDate && errors.issueDate}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                type="date"
                label="Due Date *"
                name="dueDate"
                value={values.dueDate}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.dueDate && !!errors.dueDate}
                helperText={touched.dueDate && errors.dueDate}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: "span 2" }}
              />

              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Currency *</InputLabel>
                <Select
                  name="currencyId"
                  value={values.currencyId || ""}
                  onChange={(e) => {
                    const selected = e.target.value;
                    setFieldValue("currencyId", selected);
                    const selectedCurrency = currencies.find(
                      (c) => c.id === parseInt(selected)
                    );
                    setFieldValue(
                      "exchangeRate",
                      selectedCurrency?.exchangeRate || 1.0
                    );
                  }}
                  onBlur={handleBlur}
                  error={touched.currencyId && !!errors.currencyId}
                >
                  {currencies.map((curr) => (
                    <MenuItem key={curr.id} value={curr.id}>
                      {curr.currencyName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Withholding Tax</InputLabel>
                <Select
                  name="withholdingTaxId"
                  value={values.withholdingTaxId || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.withholdingTaxId && !!errors.withholdingTaxId}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {withholdingTaxRates.map((tax) => (
                    <MenuItem key={tax.id} value={tax.id}>
                      {tax.name} ({tax.rate}%)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Exchange Rate"
                name="exchangeRate"
                value={values.exchangeRate}
                InputProps={{ readOnly: true }}
                disabled
                sx={{ gridColumn: "span 2" }}
              />
            </Box>

            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Submitting..."
                  : isEditing
                    ? "Update Invoice"
                    : "Create Invoice"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default PurchaseInvoiceForm;
